import React from 'react';
import axios from '../../../modules/utils/axios.utils';
import { AutoBulkTransaction } from '../model';
import getBrowserTimeZone from '../../../modules/shared/utils/returnTimeZone';

interface ProductsContextInterface {
  errorMessage?: string;
  exportTransactions?: (
    exportEmail: string,
    includeHeader: boolean,
    includeProducts: boolean,
    localTime: any,
    utcOffset: any,
    filterParams: string,
    filterType: string,
    yearlyOverview?: boolean,
    combineMachineTotals?: boolean,
  ) => Promise<void>;
  fetchProducts?: (combined: boolean, filterParams?: string) => Promise<void>;
  fetchProductDetails?: (filterParams?: string) => Promise<void>;
  products?: AutoBulkTransaction[];
  productsLoading?: boolean;
  productMetrics?: Record<string, string>;
}

const ProductsContext = React.createContext<ProductsContextInterface>({});

const ProductsContextConsumer = ProductsContext.Consumer;
const ProductsContextProvider: React.FC = ({ children }) => {
  const [products, setProducts] = React.useState<AutoBulkTransaction[]>([]);
  const [productsLoading, setProductsLoading] = React.useState<boolean>(true);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [productMetrics, setProductMetrics] = React.useState<Record<string, string>>({});

  const fetchProducts = async (combined: boolean, filterParams = '') => {
    setProductsLoading(true);
    setErrorMessage('');

    const cachedMachines = JSON.parse(localStorage.getItem('autoBulkMachines') || '[]');
    const cachedTimePeriod = localStorage.getItem('timePeriod') || '';
    const cachedStartDate = localStorage.getItem('startDate');
    const cachedEndDate = localStorage.getItem('endDate');

    let timeParams = {};
    const browser_tz = getBrowserTimeZone();

    if (cachedStartDate && cachedEndDate) {
      timeParams =
        cachedTimePeriod !== 'Custom'
          ? { time_period: cachedTimePeriod || '' }
          : {
              work_order_start_date_after: cachedStartDate || '',
              work_order_start_date_before: cachedEndDate || '',
            };
    } else {
      timeParams = { time_period: cachedTimePeriod || '' };
    }

    const params = new URLSearchParams({
      machine: cachedMachines.join(),
      ...Object.fromEntries(new URLSearchParams(filterParams)),
      ...timeParams,
      browser_tz,
    }).toString();

    axios
      .get<string, any>(`api/autobulk/analytics/transactions/products${combined ? '/combined' : ''}/?${params}`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('accessToken')}`,
        },
      })
      .then((response) => {
        setProducts(response.data.results);
        setProductsLoading(false);
      })
      .catch((error) => {
        setErrorMessage(error?.response?.data?.Message);
        setProducts([]);
        setProductsLoading(false);
      });
  };

  const fetchProductDetails = async (filterParams = '') => {
    setProductsLoading(true);
    setErrorMessage('');

    const cachedMachines = JSON.parse(localStorage.getItem('autoBulkMachines') || '[]');
    const cachedTimePeriod = localStorage.getItem('timePeriod') || '';
    const cachedStartDate = localStorage.getItem('startDate');
    const cachedEndDate = localStorage.getItem('endDate');

    let timeParams = {};
    const browser_tz = getBrowserTimeZone();

    if (cachedStartDate && cachedEndDate) {
      timeParams =
        cachedTimePeriod !== 'Custom'
          ? { time_period: cachedTimePeriod || '' }
          : {
              work_order_start_date_after: cachedStartDate || '',
              work_order_start_date_before: cachedEndDate || '',
            };
    } else {
      timeParams = { time_period: cachedTimePeriod || '' };
    }

    const params = new URLSearchParams({
      machine: cachedMachines.join(),
      ...Object.fromEntries(new URLSearchParams(filterParams)),
      ...timeParams,
      browser_tz,
    }).toString();

    axios
      .get<string, any>(`api/autobulk/analytics/transactions/product_details/?${params}`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('accessToken')}`,
        },
      })
      .then((response) => {
        setProducts(response.data.results);
        setProductsLoading(false);
      })
      .catch((error) => {
        setErrorMessage(error?.response?.data?.Message);
        setProducts([]);
        setProductsLoading(false);
      });
  };

  const exportTransactions = async (
    exportEmail,
    includeHeader,
    includeProducts,
    localTime,
    utcOffset,
    filterParams,
    filterType,
    yearlyOverview,
    combineMachineTotals,
  ) => {
    setProductsLoading(true);

    const cachedMachines = JSON.parse(localStorage.getItem('autoBulkMachines') || '[]');
    const cachedStartDate = localStorage.getItem('startDate');
    const cachedEndDate = localStorage.getItem('endDate');

    axios
      .post<any, any>(
        `api/autobulk/analytics/exports/products`,
        {
          cachedEndDate,
          cachedMachines,
          cachedStartDate,
          exportEmail,
          includeHeader,
          includeProducts,
          localTime,
          utcOffset,
          filterParams,
          filterType,
          yearlyOverview,
          combineMachineTotals,
        },
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` } },
      )
      .then((response) => {
        setProductsLoading(false);
      })
      .catch((error) => {
        setProductsLoading(false);
      });
  };

  return (
    <ProductsContext.Provider
      value={{
        errorMessage,
        exportTransactions,
        fetchProducts,
        fetchProductDetails,
        productMetrics,
        products,
        productsLoading,
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
};

export { ProductsContextProvider, ProductsContextConsumer, ProductsContext };
